/**
 * Catalog Service
 * Business logic layer for catalog operations
 * Wraps repository calls and provides unified interface
 */

import { Product, ShopExtended } from '../types';
import { DocumentSnapshot } from 'firebase/firestore';
import { productQueries, productSearch, productMutations } from '../../data/products';
import { shopQueries } from '../../data/shops';
import { logger } from '../../infrastructure/logger';
import { cacheManager, CACHE_TTL } from '../../utils/cache';

export interface CatalogParams {
  filter?: string;
  followingShopIds?: string[];
  selectedShopId?: string | null;
  itemsPerPage?: number;
  sortBy?: 'featured' | 'priceAsc' | 'priceDesc' | 'newest';
  searchQuery?: string;
  advancedFilters?: {
    category?: string;
    subCategory?: string;
    priceMin?: number;
    priceMax?: number;
    condition?: string;
  };
  lastDoc?: DocumentSnapshot | null;
}

export interface CatalogResult {
  products: Product[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

interface FeedItem {
  type: 'product' | 'banner' | 'injection_featured' | 'injection_banner';
  product?: Product;
  zone?: string;
  index?: number;
  products?: Product[];
}

class CatalogService {
  /**
   * Fetch catalog products based on provided parameters
   */
  async fetchCatalog(params: CatalogParams): Promise<CatalogResult> {
    const {
      filter = 'Todos',
      followingShopIds = [],
      selectedShopId = null,
      itemsPerPage = 12,
      lastDoc = null,
    } = params;

    try {
      let result;

      if (selectedShopId) {
        // Filter by specific shop
        const products = await productQueries.getProductsByShop(selectedShopId);
        result = {
          products: products.slice(0, itemsPerPage),
          lastDoc: null,
          hasMore: false,
        };
      } else if (filter === 'Usados') {
        // Used products
        const usedResult = await productQueries.getUsedProducts(itemsPerPage);
        result = {
          products: usedResult.products,
          lastDoc: usedResult.lastDoc,
          hasMore: false, // Used products don't have pagination yet
        };
      } else if (filter === 'Mis Tiendas' && followingShopIds.length > 0) {
        // Products from followed shops
        const products = await productQueries.getProductsByShopIds(followingShopIds, lastDoc, itemsPerPage);
        result = {
          products,
          lastDoc: null,
          hasMore: false, // Simple implementation for now
        };
      } else if (filter === 'Todos') {
        // All active products
        const productsResult = await productQueries.getAllProducts(lastDoc, itemsPerPage);
        result = {
          products: productsResult.products,
          lastDoc: productsResult.lastDoc,
          hasMore: true,
        };
      } else {
        // Filter by category
        const categoryResult = await productQueries.getProductsByCategory(filter, lastDoc, itemsPerPage);
        result = {
          products: categoryResult.products,
          lastDoc: categoryResult.lastDoc,
          hasMore: true,
        };
      }

      return result;
    } catch (error) {
      logger.error('Error fetching catalog', error);
      return {
        products: [],
        lastDoc: null,
        hasMore: false,
      };
    }
  }

  /**
   * Generate feed items from products with injections
   */
  generateFeedItems(
    products: Product[],
    featuredProducts: Product[],
    banners?: { localBanners: any[] } | null,
    chunkSize: number = 12,
    featuredLimit: number = 6
  ): FeedItem[] {
    const feed: FeedItem[] = [];
    const featuredSet = new Set(featuredProducts.map(p => p.id));

    for (let i = 0; i < products.length; i += chunkSize) {
      const chunkIndex = i / chunkSize;
      const chunk = products.slice(i, i + chunkSize);

      // Inject featured products every N chunks
      if (featuredSet.size > 0 && chunkIndex % 8 === 0) {
        feed.push({
          type: 'injection_featured',
          products: featuredProducts.slice(0, featuredLimit),
        });
      }

      // Inject banner every M chunks
      if (banners && banners.localBanners && banners.localBanners.length > 0 && chunkIndex % 12 === 0) {
        feed.push({
          type: 'injection_banner',
          zone: 'catalog_inline',
          index: chunkIndex / 12,
        });
      }

      // Add product items
      chunk.forEach(product => {
        feed.push({
          type: 'product',
          product,
        });
      });
    }

    return feed;
  }

  /**
   * Search products
   */
  async searchProducts(query: string, limit: number = 20): Promise<Product[]> {
    if (!query || query.length < 2) return [];

    try {
      const products = await productSearch.searchProducts(query);
      return products.slice(0, limit);
    } catch (error) {
      logger.error('Error searching products', error);
      return [];
    }
  }

  /**
   * Get featured products with caching
   */
  async getFeaturedProducts(limit: number = 12): Promise<Product[]> {
    const cacheKey = 'featured';

    return cacheManager.get<Product[]>('product', cacheKey) ||
      productQueries.getFeaturedProducts();
  }

  /**
   * Get active products with caching
   */
  async getActiveProducts(limit: number = 20): Promise<Product[]> {
    const result = await productQueries.getAllProducts(null, limit);
    return result.products;
  }

  /**
   * Get a single product by ID
   */
  async getProductById(productId: string): Promise<Product | undefined> {
    return productQueries.getProductById(productId);
  }

  /**
   * Get products by shop ID
   */
  async getProductsByShop(shopId: string): Promise<Product[]> {
    return productQueries.getProductsByShop(shopId);
  }

  /**
   * Get products by multiple IDs (batch)
   */
  async getProductsByIds(productIds: string[]): Promise<Product[]> {
    return productQueries.getProductsByIds(productIds);
  }

  /**
   * Get used products
   */
  async getUsedProducts(limit: number = 20): Promise<{ products: Product[]; lastDoc: DocumentSnapshot | null }> {
    return productQueries.getUsedProducts(limit);
  }

  /**
   * Get products by shop IDs
   */
  async getProductsByShopIds(shopIds: string[], lastDoc: DocumentSnapshot | null, limit: number): Promise<Product[]> {
    const result = await productQueries.getProductsByShopIds(shopIds, lastDoc, limit);
    return result.products;
  }

  /**
   * Get all products with pagination
   */
  async getAllProducts(lastDoc: DocumentSnapshot | null, limit: number): Promise<{ products: Product[]; lastDoc: DocumentSnapshot | null }> {
    return productQueries.getAllProducts(lastDoc, limit);
  }

  /**
   * Get products by category
   */
  async getProductsByCategory(category: string, lastDoc: DocumentSnapshot | null, limit: number): Promise<{ products: Product[]; lastDoc: DocumentSnapshot | null }> {
    return productQueries.getProductsByCategory(category, lastDoc, limit);
  }

  /**
   * Decrease product stock
   */
  async decreaseStock(productId: string, amount: number): Promise<{ success: boolean; error?: string }> {
    return productMutations.decreaseStock(productId, amount);
  }

  /**
   * Get recommended products excluding certain IDs
   */
  async getRecommendedProducts(excludeIds: string[]): Promise<Product[]> {
    return productQueries.getRecommendedProducts(excludeIds);
  }

  /**
   * Get all products (for impulse items, etc.)
   */
  async getAllProductsForDisplay(limit: number = 100): Promise<Product[]> {
    const result = await productQueries.getAllProducts(null, limit);
    return result.products;
  }
}

export const catalogService = new CatalogService();
